/**
 * Azure AD SSO authentication service.
 *
 * Routes all calls through the shared axios `api` instance so they
 * benefit from the centralised auth header injection and refresh-token
 * interceptor. The only browser-navigation step (initiateAzureLogin)
 * still uses window.location since it's a full-page redirect to the
 * Microsoft IdP.
 */

import api from './api';
import { AzureUser, AzureCallbackResponse } from '../types';

const BACKEND_URL = process.env.REACT_APP_API_URL ?? '';

class AzureAuthService {
  private getFrontendCallbackUrl(): string {
    return `${window.location.origin}/oauth2callback`;
  }

  async initiateAzureLogin(): Promise<void> {
    try {
      const callbackUrl = this.getFrontendCallbackUrl();
      const loginUrl = `${BACKEND_URL}/api/auth/azure/login?callback_url=${encodeURIComponent(callbackUrl)}`;
      window.location.href = loginUrl;
    } catch (error) {
      console.error('Azure login initiation error:', error);
      throw new Error('Failed to initiate Azure login. Please try again.');
    }
  }

  async handleAzureCallback(code: string, state?: string): Promise<AzureCallbackResponse> {
    const params: Record<string, string> = { code };
    if (state) params.state = state;
    params.callback_url = this.getFrontendCallbackUrl();

    const response = await api.get<AzureCallbackResponse>('/api/auth/azure/callback', {
      params,
    });
    return response.data;
  }

  async getCurrentUser(): Promise<AzureUser> {
    const response = await api.get<AzureUser>('/api/auth/me');
    return response.data;
  }

  async verifyToken(): Promise<boolean> {
    try {
      await api.get('/api/auth/me');
      return true;
    } catch {
      return false;
    }
  }
}

export const azureAuthService = new AzureAuthService();
export default azureAuthService;
